import { gql } from "@apollo/client";
import { CARD_PROGRAM_CAPABILITY_FIELDS } from "./cardProgram";

export const GET_COMPANY = gql`
  query company($id: ID) {
    company(id: $id) {
      id
      businessLegalName
      businessDBA
      businessCardName
      businessStructure
      businessIncorporationState
      businessEmail
      description
      establishedDate
      businessEIN
      annualRevenue
      typeOfBusiness
      phoneNumber
      companyLogoURL
      companyLogoKey
      status
      companyCurrency
      websiteURL
      isDeactivateRequested
      paymentEmail
    }
  }
`;

export const GET_COMPANIES = gql`
  query getCompanies(
    $name: String
    $searchTerm: String
    $first: Int
    $after: String
    $status: [StatusEnum]
  ) {
    getCompanies(
      name: $name
      searchTerm: $searchTerm
      first: $first
      after: $after
      status: $status
    ) {
      edges {
        node {
          id
          businessLegalName
          businessDBA
          businessCardName
          businessStructure
          businessEmail
          description
          establishedDate
          businessEIN
          typeOfBusiness
          phoneNumber
          companyLogoURL
          companyLogoKey
          status
          companyCurrency
          websiteURL
          numberOfUser
          createdAt
          isDeactivateRequested
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const ADD_NEW_COMPANY = gql`
  mutation addNewCompany($input: NewCompanyInput) {
    addNewCompany(input: $input)
  }
`;

export const UPDATE_COMPANY_PLANS = gql`
  mutation updateCompanyPlans(
    $companyId: ID!
    $input: UpdateCompanyPlansInput!
  ) {
    updateCompanyPlans(companyId: $companyId, input: $input)
  }
`;

export const REQUEST_DEACTIVATE_COMPANY = gql`
  mutation deactivateCompany($id: ID!, $reason: String) {
    deactivateCompany(id: $id, reason: $reason)
  }
`;

export const ACTIVATE_COMPANY = gql`
  mutation activateCompany(
    $id: ID!
    $input: ActivateCompanyInput
    $connectionId: ID
  ) {
    activateCompany(id: $id, input: $input, connectionId: $connectionId) {
      status
    }
  }
`;

export const ACCEPT_COMPANY_TERMS = gql`
  mutation acceptCompanyTerms($id: ID!) {
    acceptCompanyTerms(id: $id) {
      status
    }
  }
`;

export const CHECK_ACTIVATION_COMPANY_STATUS = gql`
  query checkActivationStatus($connectionId: ID) {
    checkActivationStatus(connectionId: $connectionId) {
      status
    }
  }
`;

export const UPDATE_COMPANY = gql`
  mutation updateCompany($id: ID!, $input: UpdateCompanyInput) {
    updateCompany(id: $id, input: $input) {
      id
    }
  }
`;

export const START_BANK_LINK = gql`
  mutation startBankLink($programId: ID, $type: AccountType) {
    startBankLink(programId: $programId, type: $type) {
      linkToken
      clientSecret
      accountKey
      publishableKey
    }
  }
`;

export const COMPLETE_BANK_LINK = gql`
  mutation completeBankLink($input: CompleteBankLinkInput!) {
    completeBankLink(input: $input) {
      accounts {
        id
        bankName
        last4
        status
      }
      skipped {
        name
        last4
        reason
      }
    }
  }
`;

export const GET_BALANCE = gql`
  query getBalance {
    getBalance {
      available {
        amount
        currency
      }
      pending {
        amount
        currency
      }
      outgoing {
        amount
        currency
      }
    }
  }
`;

export const GET_REWARDS_AVAILABLE = gql`
  query rewardsAvailable {
    rewardsAvailable
  }
`;

export const GET_REWARDS_BALANCE = gql`
  query getRewardsBalance {
    getRewardsBalance {
      available {
        amount
        currency
      }
      pending {
        amount
        currency
      }
      outgoing {
        amount
        currency
      }
      withheld {
        amount
        currency
      }
    }
  }
`;

export const GET_LIST_BANK_ACCOUNTS = gql`
  ${CARD_PROGRAM_CAPABILITY_FIELDS}
  query getListBankAccounts($types: [AccountType!]!, $status: [String]) {
    getListBankAccounts(types: $types, status: $status) {
      accountType
      id
      bankName
      last4
      status
      microDepositUrl
      applicationType
      connectionId
      cardProgram {
        id
        issuer
        nickname
        isDefault
        ...CardProgramCapabilityFields
      }
      details {
        nickname
        bankName
        accountNumber
        routingNumber
        accountHolderName
        bankAddress
      }
    }
  }
`;

export const GET_BANK_ACCOUNT_BALANCES = gql`
  query getBankAccountBalances($types: [AccountType!]!, $status: [String]) {
    getListBankAccounts(types: $types, status: $status) {
      id
      balance {
        available {
          amount
          currency
        }
        creditLimit {
          amount
          currency
        }
        outstanding {
          amount
          currency
        }
      }
    }
  }
`;

export const REMOVE_HOSTED_BANK_ACCOUNT = gql`
  mutation removeHostedBankAccount($accountId: String!) {
    removeHostedBankAccount(accountId: $accountId) {
      isSuccess
    }
  }
`;

export const GET_ONBOARDING_URL = gql`
  query getOnboardingUrl($connectionId: ID) {
    getOnboardingUrl(connectionId: $connectionId)
  }
`;

export const BEGIN_REWARDS_BANK_LINK = gql`
  mutation beginRewardsBankLink {
    beginRewardsBankLink {
      plaidLinkToken
    }
  }
`;

export const SAVE_REWARDS_BANK_LINK = gql`
  mutation saveRewardsBankLink(
    $plaidPublicToken: String!
    $plaidAccountIds: [String!]
  ) {
    saveRewardsBankLink(
      plaidPublicToken: $plaidPublicToken
      plaidAccountIds: $plaidAccountIds
    ) {
      accountIds
      skipped {
        name
        last4
        reason
      }
    }
  }
`;

export const GET_COMPANY_SETTINGS = gql`
  query getCompanySettings {
    getCompanySettings {
      REPORT_RETENTION_PERIOD
      CURRENCY
      CARD_ISSUER
      INTEGRATION {
        PAYMENT_METHOD_REF_ID
        TYPE
        AP_REF_ID
        ACCOUNT_REF_ID
      }
      CUSTOM_COLORS {
        primary
        secondary
        accent
        cardText
        cardArt
        cardBackground
      }
      INVOICE_NUMBER_SETTINGS {
        format
        incrementLength
        prefix
        suffix
        defaultPaymentTerms
      }
    }
  }
`;

export const GET_AUTO_BALANCE_RULES = gql`
  query getAutoBalanceRules {
    getAutoBalanceRules {
      fromAccountId
      toAccountId
      accountNumber
      companyCurrency
      targetBalance
      minimumBalance
      memo
      updatedAt
      updatedByUser {
        userId
        firstName
        lastName
        email
        role
      }
      isDisabled
    }
  }
`;

export const SET_AUTO_BALANCE_RULE = gql`
  mutation setAutoBalanceRule($input: BalanceSettingInput!) {
    setAutoBalanceRule(input: $input) {
      toAccountId
    }
  }
`;

export const REMOVE_AUTO_BALANCE_RULE = gql`
  mutation removeAutoBalanceRule($programId: ID!) {
    removeAutoBalanceRule(programId: $programId)
  }
`;

export const ENABLE_AUTO_BALANCE_RULE = gql`
  mutation enableAutoBalanceRule($programId: ID!) {
    enableAutoBalanceRule(programId: $programId) {
      toAccountId
    }
  }
`;

export const GET_MILEAGE_REIMBURSEMENT = gql`
  query getMileageReimbursement {
    getMileageReimbursement {
      rate
      type
    }
  }
`;

export const GET_DEFAULT_MILEAGE_RATES = gql`
  query getDefaultMileageRates {
    getDefaultMileageRates {
      highMileageWarningValue
      mileageRates {
        rate
        type
      }
    }
  }
`;

export const UPDATE_MILEAGE_REIMBURSEMENT = gql`
  mutation updateMileageReimbursement($input: MileageSettingInput!) {
    updateMileageReimbursement(input: $input) {
      id
    }
  }
`;

export const GET_INTEGRATION_SYNC_SETTINGS = gql`
  query getIntegrationSyncSettings {
    getIntegrationSyncSettings {
      scope
      cardPosting
      cardPostingOptions
      defaultCardPosting
      transfersAccountName

      accounts {
        accountId
        referenceId
      }
    }
  }
`;

export const GET_LEDGER_BANK_ACCOUNTS = gql`
  query getLedgerBankAccounts {
    getLedgerBankAccounts {
      id
      name
      fullName
      bankName
      accountNumber
      bankAccountLast4
    }
  }
`;

export const UPDATE_INTEGRATION_SYNC_SETTINGS = gql`
  mutation updateIntegrationSyncSettings(
    $input: IntegrationSyncSettingsInput!
  ) {
    updateIntegrationSyncSettings(input: $input) {
      scope
      cardPosting
      accounts {
        accountId
        referenceId
      }
    }
  }
`;

export const GET_COMPANY_CUSTOM_COLOR_SETTINGS = gql`
  query getCompanyCustomColorSettings {
    getCompanyCustomColorSettings {
      primary
      secondary
      accent
      cardText
      tertiary
      success
      warning
      danger
      info
      chartStrategy
      chartLeadWithSecondary
      chartThirdColor
      cardArt
      cardBackground
      cardFace
    }
  }
`;

export const UPDATE_COMPANY_CUSTOM_COLOR_SETTINGS = gql`
  mutation updateCompanyCustomColorSettings($input: CustomColorInput!) {
    updateCompanyCustomColorSettings(input: $input) {
      id
    }
  }
`;

export const GET_INVOICE_NUMBER_SETTINGS = gql`
  query getInvoiceNumberSettings {
    getInvoiceNumberSettings {
      format
      incrementLength
      prefix
      suffix
      defaultPaymentTerms
      defaultTax
      defaultTaxType
    }
  }
`;

export const UPDATE_INVOICE_NUMBER_SETTINGS = gql`
  mutation updateInvoiceNumberSettings($input: InvoiceNumberSettingsInput!) {
    updateInvoiceNumberSettings(input: $input) {
      id
    }
  }
`;

export const UPDATE_TRANSACTION_SETTING_POLICIES = gql`
  mutation updateTransactionSettingPolicies(
    $id: ID!
    $updatedCategories: [PolicyCategoryInput]
    $isPolicyTypeEnabled: Boolean
  ) {
    updateTransactionSettingPolicies(
      id: $id
      updatedCategories: $updatedCategories
      isPolicyTypeEnabled: $isPolicyTypeEnabled
    ) {
      id
    }
  }
`;

export const GET_DOCUMENT_UPLOAD_SESSION = gql`
  query getDocumentUploadSession($connectionId: ID, $programId: ID) {
    getDocumentUploadSession(
      connectionId: $connectionId
      programId: $programId
    ) {
      isComplete
      clientToken
      sessionId
      sessionEntityName
    }
  }
`;

export const GET_COMPANY_CHARGE_REPAYMENT_PROVIDER = gql`
  query getCompanyChargeRepaymentProvider($id: ID) {
    company(id: $id) {
      id
      chargeRepaymentProviderOverride
    }
  }
`;

export const SET_COMPANY_CHARGE_REPAYMENT_PROVIDER = gql`
  mutation setCompanyChargeRepaymentProvider(
    $companyId: ID!
    $provider: ChargeRepaymentProvider
  ) {
    setCompanyChargeRepaymentProvider(
      companyId: $companyId
      provider: $provider
    ) {
      id
      chargeRepaymentProviderOverride
    }
  }
`;

export const GET_COMPANY_REWARDS = gql`
  query getCompanyRewards($id: ID) {
    company(id: $id) {
      id
      settings {
        REWARDS_ENABLE
      }
    }
  }
`;

export const SET_COMPANY_REWARDS = gql`
  mutation setCompanyRewards($companyId: ID!, $rewards: Boolean!) {
    setCompanyRewards(companyId: $companyId, rewards: $rewards) {
      id
      settings {
        REWARDS_ENABLE
      }
    }
  }
`;
